// Phone dial-in: the host's browser answers callers transferred by Telnyx and mixes their audio into the room.
import { api } from "./api";

export class PhoneBridge {
  constructor(room, onChange) {
    this.room = room;
    this.onChange = onChange;
    this.calls = new Map();
    this.peerSrc = new Map();
    this.ctx = new (window.AudioContext || window.webkitAudioContext)();
    this.toRoom = this.ctx.createMediaStreamDestination();
    this.info = null;
  }

  emit() { this.onChange?.({ info: this.info, callers: [...this.calls.values()].map(({ id, number, state }) => ({ id, number, state })) }); }
  streams() { return [...this.calls.values()].map((c) => c.remote).filter(Boolean); }

  async start(mid, key) {
    this.mid = mid; this.key = key;
    const { data } = await api.post(`/public/meet/${mid}/bridge`, { key });
    const { TelnyxRTC } = await import("@telnyx/webrtc");
    const c = this.client = new TelnyxRTC({ login_token: data.token });
    c.on("telnyx.notification", (n) => { if (n.type === "callUpdate") this.update(n.call); });
    c.connect();
    this.ping = setInterval(() => api.post(`/public/meet/${mid}/bridge/ping`, { key }).catch(() => {}), 60000);
    this.info = { number: data.number, pin: data.pin };
    this.emit();
  }

  update(x) {
    let e = this.calls.get(x.id);
    if (!e && x.direction === "inbound" && x.state === "ringing") {
      e = { id: x.id, call: x, number: x.options?.remoteCallerNumber || x.options?.callerNumber || "Phone caller", state: "ringing" };
      this.calls.set(x.id, e);
      this.ctx.resume?.();
      x.answer({ audio: true, video: false });
    } else if (e && x.state === "active" && e.state !== "active") {
      e.state = "active";
      e.remote = x.remoteStream;
      e.el = new Audio(); e.el.srcObject = e.remote; e.el.play().catch(() => {});
      e.src = e.remote?.getAudioTracks().length ? this.ctx.createMediaStreamSource(e.remote) : null;
      e.dest = this.ctx.createMediaStreamDestination();
      const sn = x.peer?.instance?.getSenders().find((s) => s.track?.kind === "audio");
      sn?.replaceTrack(e.dest.stream.getAudioTracks()[0]);
      this.room.setAudioOut(this.toRoom.stream.getAudioTracks()[0]);
      this.rewire();
    } else if (e && ["hangup", "destroy"].includes(x.state)) this.drop(x.id);
    this.emit();
  }

  drop(id) {
    const e = this.calls.get(id);
    if (!e) return;
    try { e.src?.disconnect(); } catch { /* noop */ }
    if (e.el) e.el.srcObject = null;
    this.calls.delete(id);
    if (![...this.calls.values()].some((c) => c.state === "active")) this.room.setAudioOut(null);
    this.rewire();
  }

  hangup(id) { this.calls.get(id)?.call.hangup(); this.drop(id); this.emit(); }

  // Keep sources in step with the room (people joining/leaving, mic switches).
  sync(s) {
    const mic = s.local?.getAudioTracks()[0];
    if (mic?.id !== this.micId) { try { this.mic?.disconnect(); } catch { /* noop */ } this.micId = mic?.id; this.mic = mic ? this.ctx.createMediaStreamSource(new MediaStream([mic])) : null; }
    const live = new Set();
    s.peers.forEach((p) => {
      const t = p.stream?.getAudioTracks()[0];
      if (!t) return;
      live.add(p.id);
      const cur = this.peerSrc.get(p.id);
      if (cur?.tid === t.id) return;
      try { cur?.node.disconnect(); } catch { /* noop */ }
      this.peerSrc.set(p.id, { tid: t.id, node: this.ctx.createMediaStreamSource(new MediaStream([t])) });
    });
    [...this.peerSrc.keys()].filter((k) => !live.has(k)).forEach((k) => { try { this.peerSrc.get(k).node.disconnect(); } catch { /* noop */ } this.peerSrc.delete(k); });
    this.rewire();
  }

  rewire() {
    const calls = [...this.calls.values()].filter((c) => c.dest);
    const all = [this.mic, ...[...this.peerSrc.values()].map((p) => p.node), ...calls.map((c) => c.src)].filter(Boolean);
    all.forEach((n) => { try { n.disconnect(); } catch { /* noop */ } });
    this.mic?.connect(this.toRoom);
    calls.forEach((c) => {
      c.src?.connect(this.toRoom);
      this.mic?.connect(c.dest);
      this.peerSrc.forEach((p) => p.node.connect(c.dest));
      calls.forEach((o) => o !== c && o.src?.connect(c.dest));
    });
  }

  stop() {
    clearInterval(this.ping);
    [...this.calls.keys()].forEach((id) => { try { this.calls.get(id).call.hangup(); } catch { /* noop */ } this.drop(id); });
    try { this.client?.disconnect(); } catch { /* noop */ }
    if (this.mid) api.post(`/public/meet/${this.mid}/bridge/off`, { key: this.key }).catch(() => {});
    this.room.setAudioOut(null);
    this.ctx.close().catch(() => {});
  }
}
